/**
 * Hand-written control-plane client for the source-table "set parent" endpoint (#442).
 *
 * The orval generator fetches its OpenAPI spec from a running control-plane API and rewrites the
 * whole `src/api/control` folder (clean: true), so this hook lives here, outside that folder, to
 * survive regeneration. Once the spec is regenerated with the parent endpoint, this file can be
 * replaced by the generated `usePutSourceTableParent`. It intentionally mirrors the shape of the
 * generated mutation hooks (see `usePutSourceTableState`).
 */
import { useMutation } from '@tanstack/react-query';
import type {
  MutationFunction,
  QueryClient,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';

import type { ErrorProblemDetails } from './control/missionImpactAPI.schemas';
import { instance } from './mutator-control-plane';
import type { ErrorType } from './mutator-control-plane';

/**
 * Request body for setting or clearing a source table's gating parent. A non-null
 * `parentSourceTableId` gates this table behind that parent's completion; null clears the parent
 * and returns the table to running as a root.
 */
export interface SetSourceTableParentRequest {
  parentSourceTableId?: string | null;
}

/**
 * Reads a source table's parent id. The generated source-table DTO type does not yet carry
 * `parentSourceTableId` (its OpenAPI spec has not been regenerated), but the API response does
 * include it at runtime, so this reads it defensively without an unsafe cast.
 */
export function readParentSourceTableId(sourceTable: unknown): string | null {
  if (sourceTable && typeof sourceTable === 'object' && 'parentSourceTableId' in sourceTable) {
    const value = (sourceTable as { parentSourceTableId?: unknown }).parentSourceTableId;
    return typeof value === 'string' ? value : null;
  }
  return null;
}

/** Variables accepted by the set-parent mutation. */
export type PutSourceTableParentMutationVariables = {
  orgId: string;
  profileId: string;
  sourceId: string;
  sourceTableId: string;
  data: SetSourceTableParentRequest;
};

/**
 * Sets or clears the gating parent of a source table.
 * @summary Set source table parent
 */
export const putSourceTableParent = (
  orgId: string,
  profileId: string,
  sourceId: string,
  sourceTableId: string,
  setSourceTableParentRequest: SetSourceTableParentRequest,
  signal?: AbortSignal
) => {
  return instance<void>({
    url: `/orgs/${orgId}/profiles/${profileId}/sources/${sourceId}/tables/${sourceTableId}/parent`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: setSourceTableParentRequest,
    signal,
  });
};

export const getPutSourceTableParentMutationOptions = <
  TError = ErrorType<ErrorProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putSourceTableParent>>,
    TError,
    PutSourceTableParentMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putSourceTableParent>>,
  TError,
  PutSourceTableParentMutationVariables,
  TContext
> => {
  const mutationKey = ['putSourceTableParent'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putSourceTableParent>>,
    PutSourceTableParentMutationVariables
  > = (props) => {
    const { orgId, profileId, sourceId, sourceTableId, data } =
      props ?? ({} as PutSourceTableParentMutationVariables);

    return putSourceTableParent(orgId, profileId, sourceId, sourceTableId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutSourceTableParentMutationResult = NonNullable<
  Awaited<ReturnType<typeof putSourceTableParent>>
>;
export type PutSourceTableParentMutationBody = SetSourceTableParentRequest;
export type PutSourceTableParentMutationError = ErrorType<ErrorProblemDetails>;

/**
 * @summary Set source table parent
 */
export const usePutSourceTableParent = <
  TError = ErrorType<ErrorProblemDetails>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof putSourceTableParent>>,
      TError,
      PutSourceTableParentMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof putSourceTableParent>>,
  TError,
  PutSourceTableParentMutationVariables,
  TContext
> => {
  const mutationOptions = getPutSourceTableParentMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
